import { useCallback, useEffect, useMemo, useState } from "react";
import { BadgeCheck, ChevronRight, Gavel, Loader2, Plus, ShieldCheck, Trash2, Users,
  UserCheck } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader,
  DialogTitle } from "@/components/ui/dialog";
import { Chip, Empty, Kpi, Loading, PageHeader, Panel, Table, Td, Th } from "@/components/common/Chrome";
import { Nominations } from "@/components/governance/Nominations";
import { CandidateDirectory } from "@/components/governance/CandidateDirectory";
import { BallotBuilder } from "@/components/governance/BallotBuilder";
import { VotePaper } from "@/components/governance/VotePaper";
import { Results } from "@/components/governance/Results";
import { Bulletin } from "@/components/governance/Bulletin";
import { Challenges } from "@/components/governance/Challenges";
import { ArchivePanel } from "@/components/governance/ArchivePanel";
import { PostHistory } from "@/components/governance/PostHistory";
import { api, apiError } from "@/lib/api";

const STATUS_STYLE = {
  draft: "border-border bg-muted text-muted-foreground",
  initiated: "border-primary/30 bg-primary/10 text-primary",
  commission_formation: "border-amber-500/30 bg-amber-500/10 text-amber-700 dark:text-amber-300",
  commission_approved: "border-[hsl(var(--teal))]/30 bg-[hsl(var(--teal))]/10 text-[hsl(var(--teal))]",
  nominations_scheduled: "border-emerald-500/30 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300",
  nominations_open: "border-emerald-500/30 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300",
  nominations_closed: "border-amber-500/30 bg-amber-500/10 text-amber-700 dark:text-amber-300",
  counted: "border-primary/30 bg-primary/10 text-primary",
  certified: "border-primary/30 bg-primary/10 text-primary",
  declared: "border-emerald-500/30 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300",
  candidates_approved: "border-primary/30 bg-primary/10 text-primary",
  archived: "border-border bg-muted text-muted-foreground",
  active: "border-emerald-500/30 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300",
  proposed: "border-amber-500/30 bg-amber-500/10 text-amber-700 dark:text-amber-300",
  not_formed: "border-border bg-muted text-muted-foreground",
  expired: "border-border bg-muted text-muted-foreground",
  lapsed: "border-amber-500/30 bg-amber-500/10 text-amber-700 dark:text-amber-300",
  suspended: "border-red-500/30 bg-red-500/10 text-red-700 dark:text-red-300",
  resigned: "border-border bg-muted text-muted-foreground",
};
const words = (s) => String(s || "").replace(/_/g, " ");

const F = ({ label, hint, children, className }) => (
  <div className={`space-y-1.5 ${className || ""}`}>
    <Label className="text-[11.5px] text-muted-foreground">{label}</Label>
    {children}
    {hint && <p className="text-[10.5px] leading-relaxed text-muted-foreground">{hint}</p>}
  </div>
);

const Toggle = ({ label, checked, onChange, testId, disabled }) => (
  <div className="flex items-center justify-between rounded-xl border border-border px-3 py-2">
    <span className="text-[12px] text-foreground">{label}</span>
    <Switch data-testid={testId} checked={Boolean(checked)} disabled={disabled}
            onCheckedChange={onChange} />
  </div>
);

const BLANK_POSITION = { title: "", seats: 1, term_months: 24, maximum_consecutive_terms: 2,
  incumbent_may_stand: true, voting_method: "first_past_the_post", eligibility_note: "" };

export default function Elections() {
  const [cfg, setCfg] = useState(null);
  const [list, setList] = useState(null);
  const [members, setMembers] = useState(null);
  const [detail, setDetail] = useState(null);
  const [tab, setTab] = useState("elections");
  const [declare, setDeclare] = useState(null);
  const [commission, setCommission] = useState(null);
  const [newMember, setNewMember] = useState(null);
  const [rules, setRules] = useState(null);
  const [busy, setBusy] = useState("");

  const load = useCallback(async (keepRef) => {
    const [c, l, m] = await Promise.all([
      api.get("/elections/config"), api.get("/elections"), api.get("/elections/members"),
    ]);
    setCfg(c.data);
    setList(l.data);
    setMembers(m.data);
    const ref = keepRef || detail?.election_ref || l.data.items[0]?.election_ref;
    if (ref) {
      const d = await api.get(`/elections/${ref}`);
      setDetail(d.data);
      setRules(JSON.parse(JSON.stringify(d.data.rules)));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detail?.election_ref]);

  useEffect(() => { load().catch((e) => toast.error(apiError(e))); }, []); // eslint-disable-line

  const pick = async (ref) => {
    try {
      const d = await api.get(`/elections/${ref}`);
      setDetail(d.data);
      setRules(JSON.parse(JSON.stringify(d.data.rules)));
    } catch (e) { toast.error(apiError(e)); }
  };

  const run = async (key, fn, message, keepRef) => {
    setBusy(key);
    try {
      await fn();
      toast.success(message);
      await load(keepRef);
      setDeclare(null); setCommission(null); setNewMember(null);
    } catch (e) { toast.error(apiError(e)); }
    setBusy("");
  };

  const isSecretary = cfg?.you?.is_secretary;
  const isApprover = cfg?.you?.is_approver;
  const onCommission = detail?.you_are_on_commission;
  const mayCurate = cfg?.you?.may_curate_members;

  const mayEditRules = (onCommission || isSecretary) && !detail?.rules_frozen;

  const nextStage = useMemo(() => {
    if (!detail || !cfg) return "";
    const i = cfg.flow.indexOf(detail.status);
    return cfg.flow[i + 1] || "";
  }, [detail, cfg]);

  if (!cfg || !list || !members) return <Loading label="Opening the election office" />;

  return (
    <div className="space-y-6">
      <PageHeader eyebrow="Governance · Phase 6"
                  title="Elections, candidates, the ballot and the count"
                  description="The Board Secretary declares the term and the posts contested. An Election Commission of at least three eligible people runs the rest: nominations to a frozen roll, a ballot in the counting system it fixes before voting day, a secret vote, then the count, a second commissioner's certification, and the declared result on a public page."
                  actions={isSecretary && (
                    <Button size="sm" data-testid="declare-election-button"
                            className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90"
                            onClick={() => setDeclare({
                              name: "", election_type: "regular", description: "",
                              term_start: "", term_end: "", constitution_reference: "",
                              jurisdiction: "", branch: "",
                              positions: [{ ...BLANK_POSITION }] })}>
                      <Plus className="mr-1.5 h-3.5 w-3.5" /> Declare an election
                    </Button>
                  )} />

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Kpi testId="ele-kpi-live" label="Elections running" icon={Gavel}
             value={list.counters.live} sub={`${list.counters.total} in all`} />
        <Kpi testId="ele-kpi-posts" label="Posts contested" accent="violet" icon={BadgeCheck}
             value={list.counters.positions}
             sub={`${list.counters.awaiting_commission} awaiting a commission`} />
        <Kpi testId="ele-kpi-members" label="Members on the register" accent="teal" icon={Users}
             value={members.counters.total} sub={`${members.counters.active} active`} />
        <Kpi testId="ele-kpi-voting" label="Marked as voting members" accent="emerald"
             icon={UserCheck} value={members.counters.voting}
             sub={`${members.counters.paid_up} paid up`} />
      </div>

      {cfg.you.commission_seats.length > 0 && (
        <Panel title="Your Election Commission seat" testId="panel-commission-seat"
               kicker="Temporary powers, granted for this election only">
          {cfg.you.commission_seats.map((s) => (
            <div key={s.election_ref} className="text-[12.5px] text-muted-foreground"
                 data-testid={`seat-${s.election_ref}`}>
              <span className="font-semibold text-foreground">{s.name}</span> — you may{" "}
              {s.permissions.map(words).join(", ")}. These powers expire when {s.expires_when}.
            </div>
          ))}
        </Panel>
      )}

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList data-testid="ele-tabs">
          <TabsTrigger value="elections" data-testid="tab-elections">Elections</TabsTrigger>
          <TabsTrigger value="nominations" data-testid="tab-nominations">Nominations</TabsTrigger>
          <TabsTrigger value="candidates" data-testid="tab-candidates">Candidate directory</TabsTrigger>
          <TabsTrigger value="ballot" data-testid="tab-ballot">Ballot &amp; turnout</TabsTrigger>
          <TabsTrigger value="vote" data-testid="tab-vote">My ballot paper</TabsTrigger>
          <TabsTrigger value="results" data-testid="tab-results">Results</TabsTrigger>
          <TabsTrigger value="challenges" data-testid="tab-challenges">Challenges</TabsTrigger>
          <TabsTrigger value="archive" data-testid="tab-archive">Archive</TabsTrigger>
          <TabsTrigger value="holders" data-testid="tab-holders">Post history</TabsTrigger>
          <TabsTrigger value="members" data-testid="tab-members">Members register</TabsTrigger>
        </TabsList>

        <TabsContent value="challenges" className="mt-4">
          {detail ? (
            <Challenges key={`challenges-${detail.election_ref}`}
                        electionRef={detail.election_ref}
                        onChanged={() => load(detail.election_ref)} />
          ) : (
            <Panel title="Challenges and appeals" testId="panel-challenges-pick">
              <p className="text-[12.5px] text-muted-foreground">Pick an election first.</p>
            </Panel>
          )}
        </TabsContent>

        <TabsContent value="archive" className="mt-4">
          <ArchivePanel key={detail?.election_ref || "none"}
                        electionRef={detail?.election_ref || ""} status={detail?.status || ""}
                        onChanged={() => detail && load(detail.election_ref)} />
        </TabsContent>

        <TabsContent value="holders" className="mt-4">
          <PostHistory />
        </TabsContent>

        <TabsContent value="results" className="mt-4">
          {detail ? (
            <div className="space-y-4">
              <Results key={detail.election_ref} electionRef={detail.election_ref}
                       onChanged={() => load(detail.election_ref)} />
              <Bulletin key={`bulletin-${detail.election_ref}`}
                        electionRef={detail.election_ref} />
            </div>
          ) : (
            <Panel title="Results" testId="panel-results-pick">
              <p className="text-[12.5px] text-muted-foreground">Pick an election first.</p>
            </Panel>
          )}
        </TabsContent>

        <TabsContent value="ballot" className="mt-4">
          {detail ? (
            <BallotBuilder key={detail.election_ref} electionRef={detail.election_ref}
                           onChanged={() => load(detail.election_ref)} />
          ) : (
            <Panel title="Ballot" testId="panel-ballot-none">
              <p className="text-[12.5px] text-muted-foreground">Pick an election first.</p>
            </Panel>
          )}
        </TabsContent>

        <TabsContent value="vote" className="mt-4">
          {detail && ["ballot_ready", "voting_open", "voting_closed"].includes(detail.status) ? (
            <VotePaper key={detail.election_ref} electionRef={detail.election_ref}
                       onChanged={() => load(detail.election_ref)} />
          ) : (
            <Panel title="My ballot paper" testId="panel-vote-none">
              <p className="text-[12.5px] text-muted-foreground">
                There is no settled ballot on this election yet. Once the Commission freezes the
                paper your own copy appears here — and at Governance → Vote.
              </p>
            </Panel>
          )}
        </TabsContent>

        <TabsContent value="nominations" className="mt-4">
          {detail ? (
            <Nominations key={detail.election_ref} electionRef={detail.election_ref}
                         onChanged={() => load(detail.election_ref)} />
          ) : (
            <Panel title="Nominations" testId="panel-nominations-none">
              <p className="text-[12.5px] text-muted-foreground">
                Pick an election first.
              </p>
            </Panel>
          )}
        </TabsContent>

        <TabsContent value="candidates" className="mt-4">
          {detail ? (
            <CandidateDirectory key={detail.election_ref} electionRef={detail.election_ref}
                                onChanged={() => load(detail.election_ref)} />
          ) : (
            <Panel title="Candidate directory" testId="panel-candidates-none">
              <p className="text-[12.5px] text-muted-foreground">
                Pick an election first.
              </p>
            </Panel>
          )}
        </TabsContent>

        <TabsContent value="elections" className="mt-4">
          <div className="grid gap-4 lg:grid-cols-[300px_1fr]">
            <Panel title="Elections" testId="panel-election-list">
              <div className="space-y-1.5" data-testid="election-list">
                {list.items.length === 0 && (
                  <p className="text-[12px] text-muted-foreground">
                    No election has been declared yet.
                  </p>
                )}
                {list.items.map((e) => (
                  <button key={e.election_ref} type="button" data-testid={`election-${e.election_ref}`}
                          onClick={() => pick(e.election_ref)}
                          className={`flex w-full items-center justify-between rounded-xl border px-3 py-2.5 text-left transition-colors duration-150 ${
                            detail?.election_ref === e.election_ref
                              ? "border-primary/50 bg-primary/5" : "border-border hover:border-primary/30"}`}>
                    <span>
                      <span className="block text-[12.5px] font-medium text-foreground">
                        {e.name}
                      </span>
                      <span className="block text-[10.5px] text-muted-foreground">
                        {e.election_ref} · {e.type_label} · {words(e.status)}
                      </span>
                    </span>
                    <ChevronRight className="h-4 w-4 text-muted-foreground" />
                  </button>
                ))}
              </div>
            </Panel>

            {!detail ? (
              <Panel title="Nothing selected" testId="panel-no-election">
                <p className="text-[12.5px] text-muted-foreground">
                  Pick an election, or the Secretary can declare one.
                </p>
              </Panel>
            ) : (
              <div className="space-y-4">
                <Panel title={detail.name} testId="panel-election-detail"
                       kicker={`${detail.election_ref} · ${detail.type_label} · term ${detail.term_start} to ${detail.term_end}`}
                       actions={(
                         <div className="flex flex-wrap items-center gap-2">
                           <Chip className={STATUS_STYLE[detail.status]}
                                 testId="election-status">{words(detail.status)}</Chip>
                           {nextStage && nextStage !== "archived"
                             && ((onCommission && ["nominations_scheduled", "nominations_open",
                               "nominations_closed", "candidates_approved"].includes(nextStage))
                               || (isSecretary && !["nominations_scheduled", "nominations_open",
                                 "nominations_closed", "candidates_approved"].includes(nextStage))) && (
                             <Button size="sm" className="h-8 rounded-full"
                                     data-testid={`stage-${nextStage}`}
                                     disabled={nextStage === "commission_formation"
                                       || nextStage === "commission_approved"}
                                     onClick={() => run("stage",
                                       () => api.post(`/elections/${detail.election_ref}/step`,
                                         { status: nextStage }), "Election moved on",
                                       detail.election_ref)}>
                               {words(nextStage)}
                             </Button>
                           )}
                           {detail.status !== "archived" && (isSecretary || isApprover) && (
                             <Button size="sm" variant="outline" className="h-8 rounded-full"
                                     data-testid="archive-election"
                                     onClick={() => run("archive",
                                       () => api.post(`/elections/${detail.election_ref}/step`,
                                         { status: "archived",
                                           note: "Archived from the election office" }),
                                       "Election archived and the Commission's powers expired",
                                       detail.election_ref)}>
                               Archive
                             </Button>
                           )}
                         </div>
                       )}>
                  {detail.description && (
                    <p className="mb-3 text-[12.5px] leading-relaxed text-muted-foreground">
                      {detail.description}
                    </p>
                  )}
                  <div className="mb-3 flex flex-wrap gap-3 text-[11.5px] text-muted-foreground">
                    <span>Declared by {detail.declared_by_name}</span>
                    {detail.constitution_reference && (
                      <span>· {detail.constitution_reference}</span>
                    )}
                    {detail.jurisdiction && <span>· {detail.jurisdiction}</span>}
                    <span>· rules v{detail.rules_version}
                      {detail.rules_frozen ? " (frozen)" : ""}</span>
                    <span>· {detail.voter_roll.length} on the voter roll</span>
                  </div>
                  <Table testId="position-table" head={<><Th>Post</Th><Th>Seats</Th><Th>Term</Th>
                    <Th>Max terms</Th><Th>Incumbent</Th><Th>Method</Th></>}>
                    {detail.positions.map((p) => (
                      <tr key={p.position_ref} className="border-b border-border/60"
                          data-testid={`position-${p.position_ref}`}>
                        <Td>{p.title}</Td>
                        <Td className="font-mono text-[11px]">{p.seats}</Td>
                        <Td className="font-mono text-[11px]">{p.term_months} months</Td>
                        <Td className="font-mono text-[11px]">{p.maximum_consecutive_terms}</Td>
                        <Td>{p.incumbent_may_stand ? "may stand" : "may not stand"}</Td>
                        <Td>{p.method_label}</Td>
                      </tr>
                    ))}
                  </Table>
                </Panel>

                <Panel title="Election Commission" testId="panel-commission"
                       kicker={`At least ${cfg.minimum_commission} eligible people, and never anyone contesting a post`}
                       actions={(
                         <div className="flex items-center gap-2">
                           <Chip className={STATUS_STYLE[detail.commission.status]}
                                 testId="commission-status">
                             {words(detail.commission.status)}
                           </Chip>
                           {isSecretary && ["not_formed", "proposed"].includes(
                             detail.commission.status) && (
                             <Button size="sm" className="h-8 rounded-full"
                                     data-testid="propose-commission"
                                     onClick={() => setCommission({
                                       emails: detail.commission.members.map((m) => m.email),
                                       note: "" })}>
                               Propose the commission
                             </Button>
                           )}
                           {isApprover && detail.commission.status === "proposed" && (
                             <Button size="sm" variant="outline" className="h-8 rounded-full"
                                     data-testid="approve-commission"
                                     onClick={() => run("approve",
                                       () => api.post(
                                         `/elections/${detail.election_ref}/commission/approve`,
                                         { minute: "Approved by the Chair." }),
                                       "Commission approved — its temporary powers are live",
                                       detail.election_ref)}>
                               <ShieldCheck className="mr-1.5 h-3.5 w-3.5" /> Approve
                             </Button>
                           )}
                         </div>
                       )}>
                  {detail.commission.members.length === 0 ? (
                    <p className="text-[12.5px] text-muted-foreground">
                      No commissioners proposed yet — nothing can proceed without them.
                    </p>
                  ) : (
                    <div className="space-y-1.5" data-testid="commission-members">
                      {detail.commission.members.map((m) => (
                        <div key={m.email}
                             className="flex items-center justify-between border-b border-border/60 py-1.5"
                             data-testid={`commissioner-${m.email}`}>
                          <span className="text-[12.5px] text-foreground">{m.name}</span>
                          <span className="text-[11px] text-muted-foreground">
                            {words(m.office)}
                          </span>
                        </div>
                      ))}
                      <p className="pt-2 text-[11px] text-muted-foreground">
                        Powers: {(detail.commission.permissions || []).map(words).join(", ")}.
                        {detail.commission.approved_by
                          && ` Approved by ${detail.commission.approved_by}.`}
                        {detail.commission.expired_at && " These powers have expired."}
                      </p>
                    </div>
                  )}
                </Panel>

                <Panel title={`Election rulebook v${detail.rules_version}`} testId="panel-election-rules"
                       kicker={detail.rules_frozen
                         ? "Frozen when nominations were scheduled — it cannot be changed"
                         : "Set by the serving Election Commission; every save is a new version"}
                       actions={(onCommission || isSecretary) && !detail.rules_frozen && (
                         <Button size="sm" data-testid="rules-save" disabled={busy === "rules"}
                                 className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90"
                                 onClick={() => run("rules",
                                   () => api.put(`/elections/${detail.election_ref}/rules`,
                                     { ...rules, note: "Rulebook updated" }),
                                   "A new version of the rulebook is in force",
                                   detail.election_ref)}>
                           {busy === "rules" && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                           Save as a new version
                         </Button>
                       )}>
                  {rules && (
                    <div className="grid gap-4 md:grid-cols-2">
                      <div className="space-y-2.5">
                        <p className="kicker">Who may vote</p>
                        <F label="Least months in membership">
                          <Input type="number" data-testid="voter-months"
                                 disabled={!mayEditRules}
                                 value={rules.voter.minimum_membership_months}
                                 onChange={(e) => setRules({ ...rules,
                                   voter: { ...rules.voter,
                                     minimum_membership_months: Number(e.target.value) } })} />
                        </F>
                        <F label="Least age">
                          <Input type="number" data-testid="voter-age"
                                 disabled={!mayEditRules}
                                 value={rules.voter.minimum_age}
                                 onChange={(e) => setRules({ ...rules,
                                   voter: { ...rules.voter,
                                     minimum_age: Number(e.target.value) } })} />
                        </F>
                        <Toggle label="Must be paid up" testId="voter-paid"
                                disabled={!mayEditRules}
                                checked={rules.voter.must_be_paid_up}
                                onChange={(v) => setRules({ ...rules,
                                  voter: { ...rules.voter, must_be_paid_up: v } })} />
                        <p className="kicker pt-2">Who may stand</p>
                        <F label="Least months in membership to stand">
                          <Input type="number" data-testid="candidate-months"
                                 disabled={!mayEditRules}
                                 value={rules.candidate.minimum_membership_months}
                                 onChange={(e) => setRules({ ...rules,
                                   candidate: { ...rules.candidate,
                                     minimum_membership_months: Number(e.target.value) } })} />
                        </F>
                        <F label="Most posts held at once">
                          <Input type="number" data-testid="candidate-posts"
                                 disabled={!mayEditRules}
                                 value={rules.candidate.maximum_concurrent_positions}
                                 onChange={(e) => setRules({ ...rules,
                                   candidate: { ...rules.candidate,
                                     maximum_concurrent_positions: Number(e.target.value) } })} />
                        </F>
                        <Toggle label="Conflict declaration required"
                                testId="candidate-declaration" disabled={!mayEditRules}
                                checked={rules.candidate.conflict_declaration_required}
                                onChange={(v) => setRules({ ...rules,
                                  candidate: { ...rules.candidate,
                                    conflict_declaration_required: v } })} />
                      </div>
                      <div className="space-y-2.5">
                        <p className="kicker">Nominations</p>
                        <F label="Supporters needed"
                           hint="A nomination waits until this many eligible members have backed it.">
                          <Input type="number" data-testid="nomination-supporters"
                                 disabled={!mayEditRules}
                                 value={rules.nomination.minimum_supporters}
                                 onChange={(e) => setRules({ ...rules,
                                   nomination: { ...rules.nomination,
                                     minimum_supporters: Number(e.target.value) } })} />
                        </F>
                        <Toggle label="Self-nomination allowed" testId="nomination-self"
                                disabled={!mayEditRules}
                                checked={rules.nomination.self_nomination_allowed}
                                onChange={(v) => setRules({ ...rules,
                                  nomination: { ...rules.nomination,
                                    self_nomination_allowed: v } })} />
                        <Toggle label="Seconder required" testId="nomination-seconder"
                                disabled={!mayEditRules}
                                checked={rules.nomination.seconder_required}
                                onChange={(v) => setRules({ ...rules,
                                  nomination: { ...rules.nomination, seconder_required: v } })} />
                        <p className="kicker pt-2">Voting</p>
                        <F label="Counting method">
                          <Select value={rules.voting.majority} disabled={!mayEditRules}
                                  onValueChange={(v) => setRules({ ...rules,
                                    voting: { ...rules.voting, majority: v } })}>
                            <SelectTrigger data-testid="voting-method"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              {cfg.voting_methods.map((m) => (
                                <SelectItem key={m.key} value={m.key}>{m.label}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </F>
                        <F label="Least turnout (%)">
                          <Input type="number" data-testid="voting-turnout"
                                 disabled={!mayEditRules}
                                 value={rules.voting.minimum_turnout_percent}
                                 onChange={(e) => setRules({ ...rules,
                                   voting: { ...rules.voting,
                                     minimum_turnout_percent: Number(e.target.value) } })} />
                        </F>
                        <Toggle label="Secret ballot" testId="voting-secret"
                                disabled={!mayEditRules}
                                checked={rules.voting.secret_ballot}
                                onChange={(v) => setRules({ ...rules,
                                  voting: { ...rules.voting, secret_ballot: v } })} />
                        <Toggle label="Proxy voting allowed" testId="voting-proxy"
                                disabled={!mayEditRules}
                                checked={rules.voting.proxy_voting_allowed}
                                onChange={(v) => setRules({ ...rules,
                                  voting: { ...rules.voting, proxy_voting_allowed: v } })} />
                      </div>
                    </div>
                  )}
                </Panel>

                <Panel title="How this election has moved" testId="panel-timeline">
                  <div className="space-y-1.5" data-testid="election-timeline">
                    {(detail.timeline || []).map((t, i) => (
                      <div key={`${t.status}-${i}`}
                           className="flex items-start justify-between border-b border-border/60 py-1.5">
                        <span className="text-[12px] text-foreground">
                          {words(t.status)} — {t.note}
                        </span>
                        <span className="whitespace-nowrap pl-3 font-mono text-[10.5px] text-muted-foreground">
                          {t.at.slice(0, 16).replace("T", " ")}
                        </span>
                      </div>
                    ))}
                  </div>
                </Panel>
              </div>
            )}
          </div>
        </TabsContent>

        <TabsContent value="members" className="mt-4">
          <Panel title="Members register" testId="panel-members"
                 kicker="The Secretary and the serving Election Commission decide who counts as a voting member"
                 actions={mayCurate && (
                   <Button size="sm" data-testid="add-member-button"
                           className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90"
                           onClick={() => setNewMember({ name: "", email: "",
                             category: "individual", joined_on: "", paid_until: "", branch: "",
                             status: "active", voting_member: true, date_of_birth: "",
                             notes: "" })}>
                     <Plus className="mr-1.5 h-3.5 w-3.5" /> Add a member
                   </Button>
                 )}>
            <Table testId="members-table" head={<><Th>Reference</Th><Th>Member</Th><Th>Category</Th>
              <Th>Joined</Th><Th>Paid until</Th><Th>Branch</Th><Th>Status</Th><Th>Voting</Th></>}>
              {members.items.length === 0 && <Empty label="Nobody on the register yet" colSpan={8} />}
              {members.items.map((m) => (
                <tr key={m.member_ref} className="border-b border-border/60"
                    data-testid={`member-row-${m.member_ref}`}>
                  <Td className="font-mono text-[11px]">{m.member_ref}</Td>
                  <Td>
                    <div className="font-medium text-foreground">{m.name}</div>
                    <div className="text-[10.5px] text-muted-foreground">{m.email}</div>
                  </Td>
                  <Td>{m.category}</Td>
                  <Td className="font-mono text-[11px]">{m.joined_on}</Td>
                  <Td className="font-mono text-[11px]">{m.paid_until || "—"}</Td>
                  <Td>{m.branch || "—"}</Td>
                  <Td><Chip className={STATUS_STYLE[m.status]}>{m.status}</Chip></Td>
                  <Td>
                    <Switch data-testid={`voting-${m.member_ref}`} disabled={!mayCurate}
                            checked={Boolean(m.voting_member)}
                            onCheckedChange={(v) => run(`vote-${m.member_ref}`,
                              () => api.patch(`/elections/members/${m.member_ref}`,
                                { voting_member: v }),
                              v ? `${m.name} is now a voting member`
                                : `${m.name} is no longer a voting member`)} />
                  </Td>
                </tr>
              ))}
            </Table>
          </Panel>
        </TabsContent>
      </Tabs>

      {/* declare an election */}
      <Dialog open={Boolean(declare)} onOpenChange={(o) => !o && setDeclare(null)}>
        <DialogContent className="max-h-[88vh] max-w-3xl overflow-y-auto"
                       data-testid="declare-dialog">
          <DialogHeader><DialogTitle>Declare an election</DialogTitle>
            <DialogDescription>
              You are declaring the term and the posts contested. An Election Commission has to be
              approved before anything else can happen.
            </DialogDescription></DialogHeader>
          <div className="grid gap-3 md:grid-cols-2">
            <F label="Name" className="md:col-span-2">
              <Input data-testid="election-name" value={declare?.name || ""}
                     onChange={(e) => setDeclare({ ...declare, name: e.target.value })} />
            </F>
            <F label="Kind of election">
              <Select value={declare?.election_type}
                      onValueChange={(v) => setDeclare({ ...declare, election_type: v })}>
                <SelectTrigger data-testid="election-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {cfg.election_types.map((t) => (
                    <SelectItem key={t.key} value={t.key}>{t.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </F>
            <F label="Constitution or bylaws reference">
              <Input data-testid="election-constitution"
                     value={declare?.constitution_reference || ""}
                     onChange={(e) => setDeclare({ ...declare,
                       constitution_reference: e.target.value })} />
            </F>
            <F label="Term starts">
              <Input type="date" data-testid="term-start" value={declare?.term_start || ""}
                     onChange={(e) => setDeclare({ ...declare, term_start: e.target.value })} />
            </F>
            <F label="Term ends">
              <Input type="date" data-testid="term-end" value={declare?.term_end || ""}
                     onChange={(e) => setDeclare({ ...declare, term_end: e.target.value })} />
            </F>
            <F label="Jurisdiction">
              <Input data-testid="election-jurisdiction" value={declare?.jurisdiction || ""}
                     onChange={(e) => setDeclare({ ...declare, jurisdiction: e.target.value })} />
            </F>
            <F label="Branch or chapter">
              <Input data-testid="election-branch" value={declare?.branch || ""}
                     onChange={(e) => setDeclare({ ...declare, branch: e.target.value })} />
            </F>
            <F label="Description" className="md:col-span-2">
              <Textarea rows={2} data-testid="election-description"
                        value={declare?.description || ""}
                        onChange={(e) => setDeclare({ ...declare,
                          description: e.target.value })} />
            </F>
          </div>
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <p className="kicker">Posts contested</p>
              <Button size="sm" variant="outline" className="h-8 rounded-full"
                      data-testid="add-position"
                      onClick={() => setDeclare({ ...declare,
                        positions: [...declare.positions, { ...BLANK_POSITION }] })}>
                <Plus className="mr-1.5 h-3.5 w-3.5" /> Another post
              </Button>
            </div>
            {(declare?.positions || []).map((p, idx) => (
              <div key={`pos-${idx}`} className="grid gap-2 rounded-xl border border-border p-3 md:grid-cols-6"
                   data-testid={`position-form-${idx}`}>
                <F label="Post" className="md:col-span-2">
                  <Input data-testid={`position-title-${idx}`} value={p.title}
                         onChange={(e) => {
                           const positions = [...declare.positions];
                           positions[idx] = { ...p, title: e.target.value };
                           setDeclare({ ...declare, positions });
                         }} />
                </F>
                <F label="Seats">
                  <Input type="number" data-testid={`position-seats-${idx}`} value={p.seats}
                         onChange={(e) => {
                           const positions = [...declare.positions];
                           positions[idx] = { ...p, seats: Number(e.target.value) };
                           setDeclare({ ...declare, positions });
                         }} />
                </F>
                <F label="Term (months)">
                  <Input type="number" data-testid={`position-term-${idx}`} value={p.term_months}
                         onChange={(e) => {
                           const positions = [...declare.positions];
                           positions[idx] = { ...p, term_months: Number(e.target.value) };
                           setDeclare({ ...declare, positions });
                         }} />
                </F>
                <F label="Counting method" className="md:col-span-2">
                  <div className="flex items-center gap-2">
                    <Select value={p.voting_method}
                            onValueChange={(v) => {
                              const positions = [...declare.positions];
                              positions[idx] = { ...p, voting_method: v };
                              setDeclare({ ...declare, positions });
                            }}>
                      <SelectTrigger data-testid={`position-method-${idx}`}><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {cfg.voting_methods.map((m) => (
                          <SelectItem key={m.key} value={m.key}>{m.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {declare.positions.length > 1 && (
                      <Button size="icon" variant="ghost" className="h-9 w-9"
                              data-testid={`remove-position-${idx}`}
                              onClick={() => setDeclare({ ...declare,
                                positions: declare.positions.filter((_, i) => i !== idx) })}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </F>
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button data-testid="declare-save" disabled={busy === "declare"}
                    className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90"
                    onClick={() => run("declare", async () => {
                      const res = await api.post("/elections", declare);
                      await load(res.data.election_ref);
                    }, "Election declared — now form the Election Commission")}>
              {busy === "declare" && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Declare it
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* propose the commission */}
      <Dialog open={Boolean(commission)} onOpenChange={(o) => !o && setCommission(null)}>
        <DialogContent className="max-h-[85vh] max-w-xl overflow-y-auto"
                       data-testid="commission-dialog">
          <DialogHeader><DialogTitle>Propose the Election Commission</DialogTitle>
            <DialogDescription>
              At least {cfg.minimum_commission} eligible people. Nobody contesting a post may
              serve. The Chair has to approve them before their powers begin.
            </DialogDescription></DialogHeader>
          <div className="space-y-1.5" data-testid="commission-picker">
            {(detail?.eligible_commission || []).map((p) => {
              const on = (commission?.emails || []).includes(p.email);
              return (
                <button key={p.email} type="button" data-testid={`pick-${p.email}`}
                        onClick={() => setCommission({ ...commission,
                          emails: on ? commission.emails.filter((e) => e !== p.email)
                            : [...commission.emails, p.email] })}
                        className={`flex w-full items-center justify-between rounded-xl border px-3 py-2 text-[12px] transition-colors duration-150 ${
                          on ? "border-primary/50 bg-primary/10 text-foreground"
                            : "border-border text-muted-foreground hover:border-primary/30"}`}>
                  <span>{p.name}</span>
                  <span className="text-[10.5px] uppercase tracking-wider">{words(p.office)}</span>
                </button>
              );
            })}
          </div>
          <p className="text-[11.5px] text-muted-foreground" data-testid="commission-count">
            {(commission?.emails || []).length} chosen of at least {cfg.minimum_commission}
          </p>
          <F label="Note">
            <Textarea rows={2} data-testid="commission-note" value={commission?.note || ""}
                      onChange={(e) => setCommission({ ...commission, note: e.target.value })} />
          </F>
          <DialogFooter>
            <Button data-testid="commission-save" disabled={busy === "commission"}
                    className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90"
                    onClick={() => run("commission",
                      () => api.post(`/elections/${detail.election_ref}/commission`, commission),
                      "Commission proposed — it now needs the Chair's approval",
                      detail.election_ref)}>
              {busy === "commission" && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Propose
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* add a member */}
      <Dialog open={Boolean(newMember)} onOpenChange={(o) => !o && setNewMember(null)}>
        <DialogContent className="max-w-2xl" data-testid="member-dialog">
          <DialogHeader><DialogTitle>Add a member</DialogTitle>
            <DialogDescription>
              Only an active member can be marked as a voting member.
            </DialogDescription></DialogHeader>
          <div className="grid gap-3 md:grid-cols-2">
            <F label="Name">
              <Input data-testid="member-name" value={newMember?.name || ""}
                     onChange={(e) => setNewMember({ ...newMember, name: e.target.value })} />
            </F>
            <F label="Email">
              <Input data-testid="member-email" value={newMember?.email || ""}
                     onChange={(e) => setNewMember({ ...newMember, email: e.target.value })} />
            </F>
            <F label="Category">
              <Select value={newMember?.category}
                      onValueChange={(v) => setNewMember({ ...newMember, category: v })}>
                <SelectTrigger data-testid="member-category"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {cfg.member_categories.map((c) => (
                    <SelectItem key={c} value={c}>{c}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </F>
            <F label="Branch">
              <Input data-testid="member-branch" value={newMember?.branch || ""}
                     onChange={(e) => setNewMember({ ...newMember, branch: e.target.value })} />
            </F>
            <F label="Joined on">
              <Input type="date" data-testid="member-joined" value={newMember?.joined_on || ""}
                     onChange={(e) => setNewMember({ ...newMember, joined_on: e.target.value })} />
            </F>
            <F label="Paid until">
              <Input type="date" data-testid="member-paid" value={newMember?.paid_until || ""}
                     onChange={(e) => setNewMember({ ...newMember,
                       paid_until: e.target.value })} />
            </F>
            <F label="Date of birth">
              <Input type="date" data-testid="member-dob" value={newMember?.date_of_birth || ""}
                     onChange={(e) => setNewMember({ ...newMember,
                       date_of_birth: e.target.value })} />
            </F>
            <Toggle label="Voting member" testId="member-voting"
                    checked={newMember?.voting_member}
                    onChange={(v) => setNewMember({ ...newMember, voting_member: v })} />
          </div>
          <DialogFooter>
            <Button data-testid="member-save" disabled={busy === "member"}
                    className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90"
                    onClick={() => run("member",
                      () => api.post("/elections/members", newMember), "Member added")}>
              {busy === "member" && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Add them
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
